<template>
  <b-card no-body header-tag="header" footer-tag="footer">
    <template slot="header">
      <div class="total-count">
        {{ totalRows }}
        <span class="label">Results found</span>
      </div>

      <!-- Search Box -->
      <div class="search-box" :class="{active: filter}">
        <div class="input-group">
          <div class="input-group-prepend">
            <div class="input-group-text">
              <font-awesome-icon icon="search" fixed-width class="icon" />
            </div>
          </div>
          <b-form-input
            type="text"
            v-model="filter"
            placeholder="Find a result by keyword..."
            @keyup="searchResults"
          />
          <button class="clear-search" @click="clearSearch()">
            <font-awesome-icon icon="times-circle" fixed-width class="icon" />
          </button>
        </div>
      </div>
    </template>

    <b-card-body>
      <!-- Per page selection -->
      <div class="form-inline per-page-selection d-flex align-items-center float-left">
        <b-form-select :options="pageOptions" v-model="perPage" @change="changePageSize" />
        <span class="ml-2 per-page-label">Results Per Page</span>
      </div>
      <div class="float-right">
        <button
          class="btn btn-sm btn-outline-secondary ml-2"
          v-if="totalRows < 1000 && totalRows > 0"
          @click="csvDownload"
        >
          <font-awesome-icon icon="file-export" class="icon" v-if="!downloading" />
          <font-awesome-icon icon="spinner" class="icon" spin v-if="downloading" />Export CSV
        </button>
      </div>
    </b-card-body>

    <!-- Results Table -->
    <b-table
      ref="resultsTable"
      show-empty
      striped
      responsive
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :items="doResultSearch"
      :fields="fields"
      :current-page="currentPage"
      :per-page="perPage"
      :filter="filter"
      @filtered="onFiltered"
      :busy="loading"
      empty-text="There are no results to display"
    >
      <template slot="top-row" slot-scope="data">
        <td v-for="field in data.fields" :key="field.filterKey" :class="field.tdClass">
          <input
            type="text"
            @keyup="updateColumnFilters()"
            class="form-control form-control-sm"
            v-if="field.filterType == 'text'"
            v-model="filters[field.filterKey]"
          />
        </td>
      </template>

      <!-- Lab Ref -->
      <template slot="labSampleReference" slot-scope="row">
        <router-link tag="a" :to="`/jobs/${ row.item.sample.job.jobId }`" v-if="jobId === 'all'">
          <strong>
            {{ row.item.labSampleReference }}
            <font-awesome-icon icon="angle-right" fixed-width class="icon" />
          </strong>
        </router-link>
        <!-- Dont link if on Job Detail page -->
        <span v-else>{{ row.item.labSampleReference }}</span>
      </template>

      <!-- Sample Ref -->
      <template slot="clientSampleReference" slot-scope="row">
        <router-link
          tag="a"
          :to="`/jobs/${ row.item.sample.job.jobId }/samples/${row.item.sample.sampleId}`"
          v-if="sampleId == -1"
        >
          <strong>
            {{ row.item.sample.clientSampleReference }}
            <font-awesome-icon icon="angle-right" fixed-width class="icon" />
          </strong>
        </router-link>
        <!-- Dont link if on Sample Detail page -->
        <span v-else>{{ row.item.sample.clientSampleReference }}</span>
      </template>

      <!-- Procedure -->
      <template slot="procedureName1" slot-scope="row">
        <span
          v-html="`${row.item.procedureName1} ${row.item.procedureName2}`.replace(/(\r\n)|\n/g, '<br>')"
        />
      </template>

      <!-- Analyte -->
      <template slot="analyteName1" slot-scope="row">
        <span
          v-html="`${row.item.analyteName1} ${row.item.analyteName2}`.replace(/(\r\n)|\n/g, '<br>')"
        />
      </template>

      <!-- Result -->
      <template slot="textResult" slot-scope="row">
        {{ row.item.textResult }}
        <ul v-if="row.item.results" class="list-unstyled">
          <li
            v-for="(component,componentIndex) in row.item.results"
            :key="componentIndex"
          >{{ component.textResult }}</li>
        </ul>
      </template>

      <!-- Unit -->
      <template slot="units" slot-scope="row">
        <span v-html="row.item.units ? `${row.item.units}`.replace(/(\r\n)|\n/g, '<br>') : ''" />
      </template>
    </b-table>

    <!-- Footer -->
    <template slot="footer">
      <div class="d-flex align-items-center justify-content-between">
        <span>Page {{ currentPage }} of {{ totalPages }}</span>
        <b-pagination :total-rows="totalRows" :per-page="perPage" v-model="currentPage" />
      </div>
      <small>The Certificate of Analysis provided by Analytica Laboratories Limited T/A ALS NZ takes precedence over results displayed on or downloaded from this site.</small>
    </template>
  </b-card>
</template>

<style lang="scss">
@import "../assets/styles/main.scss";
</style>

<script>
import { searchJobResults, searchSampleResults } from "@/services/api";
import { EventBus } from "@/services/event-bus.js";
import { getResultSort, storeResultSort } from "@/services/localstorage";
const moment = require("moment");
const Json2csvParser = require("json2csv").Parser;

export default {
  name: "ResultsTable",
  components: {},
  props: {
    jobId: {
      type: String,
      default: () => "all"
    },
    sampleId: {
      type: Number,
      default: () => -1
    },
    selectedClients: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      downloading: false,
      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      pageOptions: [20, 50, 100, 500],
      filter: null,
      totalPages: 0,
      searchactive: false,
      filters: {
        labSampleReference: "",
        clientSampleReference: "",
        procedureName: "",
        analyteName: "",
        textResult: "",
        units: ""
      },
      fields: [
        {
          key: "labSampleReference",
          label: "Lab Reference",
          sortable: true,
          filterType: "text",
          filterKey: "labSampleReference"
        },
        {
          key: "clientSampleReference",
          label: "Sample Reference",
          sortable: true,
          filterType: "text",
          filterKey: "clientSampleReference"
        },
        {
          key: "procedureName1",
          label: "Procedure",
          sortable: true,
          filterType: "text",
          filterKey: "procedureName"
        },
        {
          key: "analyteName1",
          label: "Analyte",
          sortable: true,
          filterType: "text",
          filterKey: "analyteName"
        },
        {
          key: "textResult",
          label: "Result",
          sortable: true,
          filterType: "text",
          filterKey: "textResult"
        },
        {
          key: "units",
          label: "Units",
          sortable: true,
          filterType: "text",
          filterKey: "units"
        }
      ],
      results: [],
      sortBy: null,
      sortDesc: true
    };
  },
  watch: {
    selectedClients: function() {
      this.refreshResultTable();
    },
    loading() {
      if (this.loading) {
        EventBus.$emit("showLoader");
      } else {
        EventBus.$emit("hideLoader");
      }
    }
  },
  methods: {
    updateColumnFilters() {
      this.currentPage = 1;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.refreshResultTable();
      }, 500);
    },
    changePageSize(value) {
      this.currentPage = 1;
      this.perPage = value;
      this.refreshResultTable();
    },
    changePage(page) {
      this.currentPage = page;
      this.refreshResultTable();
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    clearSearch() {
      this.filter = "";
      this.refreshResultTable();
    },
    searchResults() {
      this.currentPage = 1;
      clearTimeout(this.timeout);
      let self = this;
      this.timeout = setTimeout(() => {
        if (self.filter.length < 1) {
          self.clearSearch();
        }
        self.refreshResultTable();
      }, 500);
    },
    async doResultSearch(ctx) {
      // show loader
      this.loading = true;
      let params = this.makeParams();
      storeResultSort(params);
      params = this.filterMarkup(params);

      const result = await searchJobResults(this.jobId, params);

      this.perPage = result.pagination.pageSize;
      this.totalRows = result.pagination.totalResults;
      this.totalPages = Math.ceil(this.totalRows / this.perPage);

      // hide loader
      this.loading = false;

      return result.results;
    },
    makeParams() {
      const params = {
        page: this.currentPage,
        pageSize: this.perPage,
        orderFields: [],
        searchFields: {
          //'clientOrderNumber': 'rdNoH8NT5'
        }
      };
      if (!this.sortBy) {
        params.orderFields.push({
          field: "jobId",
          direction: "DESC"
        });
      } else {
        params.orderFields.push({
          field: this.sortBy.replace("procedureName1", "procedureName"),
          direction: this.sortDesc ? "DESC" : "ASC"
        });
      }

      // Override the sorting if it's on the labSampleReference column
      if (this.sortBy === "labSampleReference") {
        params.orderFields = [
          {
            field: "job_labRegistrationReference",
            direction: this.sortDesc ? "DESC" : "ASC"
          },
          {
            field: "sampleNumber",
            direction: this.sortDesc ? "DESC" : "ASC"
          }
        ];
      }

      // Override the sorting if it's on the textResult column
      if (this.sortBy === "textResult") {
        params.orderFields = [
          {
            field: "componentType",
            direction: this.sortDesc ? "DESC" : "ASC"
          },
          {
            field: "textResult",
            direction: this.sortDesc ? "DESC" : "ASC"
          }
        ];
      }

      if (this.filter && this.filter.trim() !== "") {
        params.textSearch = this.filter;
      }

      if (this.jobId === "all" && this.selectedClients.length > 0) {
        params.searchFields.clients = this.selectedClients.map(
          client => client.id
        );
      }

      if (this.sampleId !== -1) {
        params.searchFields.sampleId = this.sampleId;
      }

      // Column Filters
      this.fields.forEach(field => {
        let key = field.filterKey;
        if (field.filterType === "text") {
          if (this.filters[key] && this.filters[key].trim())
            params.searchFields[key] = this.filters[key].trim();
        } else if (field.filterType === "date") {
          if (this.filters[key])
            params.searchFields[key] = [
              {
                value: this.filters[key],
                operator: "="
              }
            ];
        }
      });

      return params;
    },
    restoreParams(config) {
      // always setting this to one - pagination won't set it properly for some reason.
      this.currentPage = 1; //config.page || 1;
      this.perPage = config.pageSize || 20;
      this.filter = config.textSearch || "";

      if (config.orderFields && config.orderFields.length > 0) {
        if (config.orderFields[0].field) {
          this.sortBy = config.orderFields[0].field.replace("_", ".");
          this.sortDesc = true;
          if (config.orderFields[0].direction !== "DESC") {
            this.sortDesc = false;
          }
        }
      }

      // now the column filters - a wee bit trickier
      if (config.searchFields) {
        const keys = Object.keys(config.searchFields);
        keys.forEach(key => {
          // find the object in the fields array
          let field = null;
          this.fields.forEach(f => {
            if (f.filterKey === key) {
              field = f;
            }
          });

          if (field) {
            if (field.filterType) {
              switch (field.filterType) {
                case "number":
                case "date-range":
                  if (config.searchFields[key].length > 0) {
                    this.filters[key][0] = config.searchFields[key][0].value;

                    if (config.searchFields[key][0].isFirst) {
                      this.filters[key][0] = "";
                      this.filters[key][1] = config.searchFields[key][0].value;
                      break;
                    }
                  } else {
                    this.filters[key][0] = "";
                  }

                  if (config.searchFields[key].length > 1) {
                    this.filters[key][1] = config.searchFields[key][1].value;
                  } else {
                    this.filters[key][1] = "";
                  }
                  break;
                case "date":
                  this.filters[key] = config.searchFields[key].value;
                  break;
                case "select":
                case "text":
                default:
                  this.filters[key] = config.searchFields[key];
                  break;
              }
            } else {
              this.filters[key] = config.searchFields[key];
            }
          }
        });
      }
    },
    filterMarkup(params) {
      // Have to add some marker fields to the params in order to be able to restore them,
      // and this then removes those fields so that the search does not fail.
      this.fields.forEach(field => {
        let key = field.filterKey;
        if (Array.isArray(params.searchFields[key])) {
          if (params.searchFields[key].length > 0) {
            for (let i = 0; i < params.searchFields[key].length; i++) {
              let p = params.searchFields[key][i];
              if (p.isFirst) {
                delete p.isFirst;
              }
            }
          }
        }
      });

      return params;
    },
    forceFileDownload(response, filename) {
      const blob = new Blob([response]);

      //for microsoft IE
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        // no filename in response :(
        link.setAttribute("download", filename); // have to specify a filename here
        document.body.appendChild(link);
        this.downloading = false;
        link.click();
      }
    },
    async csvDownload() {
      this.downloading = true;
      const promises = [];
      const paramsTemplate = this.makeParams();
      const pages = Math.ceil(this.totalRows / 500);
      for (let i = 0; i <= pages; i++) {
        const params = JSON.parse(JSON.stringify(paramsTemplate));
        params.page = i + 1;
        params.pageSize = 500;
        promises.push(searchJobResults(this.jobId, params));
      }
      const results = await Promise.all(promises);
      const rows = results.reduce((accumulator, resultSet) => {
        resultSet.results.forEach(result => {
          Object.keys(result.sample).forEach(sampleKey => {
            result[`sample.${sampleKey}`] = result.sample[sampleKey];
          });
          Object.keys(result["sample.job"]).forEach(jobKey => {
            result[`job.${jobKey}`] = result["sample.job"][jobKey];
          });
          delete result["sample.job"];
          delete result.sample;
          accumulator.push(result);
        });
        return accumulator;
      }, []);
      const fields = Object.keys(rows[0]);
      try {
        const opts = { fields };
        const parser = new Json2csvParser(opts);
        const csv = parser.parse(rows);
        this.forceFileDownload(
          csv,
          `analytica_results_search_exported_${moment().format(
            "YYYY_MM_DD_HH_mm_ss"
          )}.csv`
        );
      } catch (err) {
        console.error(err);
      }

      this.downloading = false;
    },
    refreshResultTable() {
      //if(this.$refs.resultsTable && /^\/(results)/.test(this.$root._route.fullPath))
      this.$refs.resultsTable.refresh();
    }
  },
  mounted() {
    const sortConfig = getResultSort();
    if (sortConfig) {
      this.restoreParams(sortConfig);
    }

    this.refreshResultTable();
  }
};
</script>



// WEBPACK FOOTER //
// src/components/ResultsTable.vue